import type { AxiosError } from 'axios'

export const BRIGHTSITES_EXTENSION_MESSAGE_SOURCE = 'bookkeeper-brightsites-extension'

export type BrightSitesExtensionErrorCode =
  | 'NOT_INSTALLED'
  | 'LOGIN_REQUIRED'
  | 'EXTRACTION_FAILED'
  | 'TIMEOUT'

export type BrightSitesExtensionTokens = {
  jwt: string
  cookie: string
}

export class BrightSitesExtensionError extends Error {
  code: BrightSitesExtensionErrorCode

  constructor(code: BrightSitesExtensionErrorCode, message: string) {
    super(message)
    this.name = 'BrightSitesExtensionError'
    this.code = code
  }
}

type ExtensionMessage = {
  source?: string
  type?: string
  requestId?: string
  success?: boolean
  code?: BrightSitesExtensionErrorCode
  error?: string
  tokens?: BrightSitesExtensionTokens
}

function isExtensionMessage(data: unknown): data is ExtensionMessage {
  return (
    !!data &&
    typeof data === 'object' &&
    'source' in data &&
    (data as ExtensionMessage).source === BRIGHTSITES_EXTENSION_MESSAGE_SOURCE
  )
}

if (typeof window !== 'undefined') {
  window.addEventListener('message', (event) => {
    if (event.origin !== window.location.origin || !isExtensionMessage(event.data)) {
      return
    }
    if (event.data.type === 'BRIGHTSITES_EXTENSION_READY') {
      return
    }
  })
}

export async function requestBrightSitesTokensFromExtension(
  timeoutMs = 15000
): Promise<BrightSitesExtensionTokens> {
  const requestId = crypto.randomUUID()

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage)
      reject(
        new BrightSitesExtensionError(
          'NOT_INSTALLED',
          'Bright Sites Token Extractor extension did not respond.'
        )
      )
    }, timeoutMs)

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !isExtensionMessage(event.data)) {
        return
      }
      if (
        event.data.type !== 'BRIGHTSITES_EXTRACT_TOKENS_RESPONSE' ||
        event.data.requestId !== requestId
      ) {
        return
      }

      window.clearTimeout(timer)
      window.removeEventListener('message', onMessage)

      if (event.data.success && event.data.tokens) {
        resolve(event.data.tokens)
        return
      }

      const code = event.data.code ?? 'EXTRACTION_FAILED'
      const message =
        event.data.error ??
        (code === 'LOGIN_REQUIRED'
          ? 'Please log into Bright Sites, then try again.'
          : 'Failed to extract Bright Sites tokens.')

      reject(new BrightSitesExtensionError(code, message))
    }

    window.addEventListener('message', onMessage)
    window.postMessage(
      {
        source: BRIGHTSITES_EXTENSION_MESSAGE_SOURCE,
        type: 'BRIGHTSITES_EXTRACT_TOKENS_REQUEST',
        requestId,
      },
      window.location.origin
    )
  })
}

export function isBrightSitesAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return false
  }
  const axiosError = error as AxiosError
  const header = axiosError.response?.headers?.['x-external-auth-error']
  if (typeof header === 'string') {
    return header.toLowerCase() === 'brightsites'
  }
  const detail = axiosError.response?.data as { detail?: unknown } | undefined
  if (typeof detail?.detail === 'string') {
    return detail.detail.toLowerCase().includes('bright sites')
  }
  return false
}
